import React, { useContext, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    TimePickerContainer,
    TimePickerContent,
    TimePickerRow,
    TimePickerLabel,
} from './TimePicker.style';
import { TimeSelector } from './TimeSelector';
import { NextButton } from '../../MobileFragments/NextButton';
import { DateUtils } from '../../../../utils';
import { useTimeConstraints } from '../../../../utils/hooks/useTimeConstraints';
import { setDepartureTime } from '../../../../redux/currentSearch/actions';
import { getSegmentById } from '../../../../redux/currentSearch/selectors';
import { MINUTE_SLOTS } from './constants';
import { TrackingUtils } from '../../../../utils/TrackingUtils';
import { PageContext } from '../../../../utils/PageContext/PageContext';

export const TimePicker = ({ isVisible, extraTracking, segmentId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const hasTrackedOnLoad = useRef(false);

    const segmentQuery = useMemo(() => ({ segmentId }), [segmentId]);
    const segment = useSelector((state) => getSegmentById(state, segmentQuery));
    const storedDepartureTime = segment?.departureTime ?? null;
    const segmentDate = segment?.date;

    const parsedDate = useMemo(() => {
        if (!segmentDate) return null;
        return typeof segmentDate === 'string' ? DateUtils.stringToDate(segmentDate) : segmentDate;
    }, [segmentDate]);

    const isToday = parsedDate ? DateUtils.isToday(parsedDate) : false;

    const { hours, minutes, validTime, minimumHour, minimumMinute } =
        useTimeConstraints(isToday, storedDepartureTime, MINUTE_SLOTS, parsedDate);

    const { tracking } = useContext(PageContext);

    useEffect(() => {
        if (segmentId != null && validTime !== storedDepartureTime) {
            dispatch(setDepartureTime(segmentId, validTime));
        }
    }, [dispatch, segmentId, validTime, storedDepartureTime]);

    // Track onLoad of the component
    useEffect(() => {
        if (isVisible && !hasTrackedOnLoad.current) {
            TrackingUtils.trackEvent({
                category: tracking.category,
                action: TrackingUtils.ACTION.SEARCH_BY_HOUR,
                label: TrackingUtils.LABEL.searchByHourOnLoad(hours, minutes),
                page: tracking.page,
            });
            hasTrackedOnLoad.current = true;
        }
    }, [isVisible, tracking, hours, minutes]);

    const handleHoursChange = (value) => {
        TrackingUtils.trackEvent({
            category: tracking.category,
            action: TrackingUtils.ACTION.SEARCH_BY_HOUR,
            label: TrackingUtils.LABEL.searchByHourClickHour(hours, minutes, value),
            page: tracking.page,
        });
        dispatch(setDepartureTime(segmentId, `${value}${minutes}`));
    };
    const handleMinutesChange = (value) => {
        TrackingUtils.trackEvent({
            category: tracking.category,
            action: TrackingUtils.ACTION.SEARCH_BY_HOUR,
            label: TrackingUtils.LABEL.searchByHourClickMinute(hours, minutes, value),
            page: tracking.page,
        });
        dispatch(setDepartureTime(segmentId, `${hours}${value}`));
    };

    const containerClassName = classNames('prisma-elevation-1', {
        'time-picker-visible': isVisible,
    });

    return (
        <TimePickerContainer className={containerClassName}>
            <TimePickerContent className="odf-space-inner-l">
                <TimePickerRow>
                    <TimePickerLabel
                        className={
                            'prisma-text-lg odf-strong odf-text odf-text-neutral-700'
                        }
                    >
                        {t('searchByHour.timeSelector.label')}
                    </TimePickerLabel>
                    <TimeSelector
                        hours={hours}
                        minutes={minutes}
                        minimumHour={minimumHour}
                        minimumMinute={minimumMinute}
                        onHoursChange={handleHoursChange}
                        onMinutesChange={handleMinutesChange}
                    />
                </TimePickerRow>
                <NextButton extraTracking={extraTracking} testId="dates-next-btn" isLargeVersion />
            </TimePickerContent>
        </TimePickerContainer>
    );
};

TimePicker.propTypes = {
    isVisible: PropTypes.bool,
    extraTracking: PropTypes.func,
    segmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

TimePicker.defaultProps = {
    isVisible: false,
    extraTracking: () => {},
    segmentId: null,
};

export default TimePicker;
